/**
 * Data Catalog Helper Functions
 * Pure utility functions without state dependencies
 */

import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ScienceIcon from '@mui/icons-material/Science';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AirIcon from '@mui/icons-material/Air';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RadarIcon from '@mui/icons-material/Radar';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import BiotechIcon from '@mui/icons-material/Biotech';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import TimelineIcon from '@mui/icons-material/Timeline';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

/**
 * Tag icon options for category customization
 */
/**
 * Der serverseitige Sonderwert fuer "kein Archetyp zugeordnet" (#788).
 *
 * Er steht hier und nicht im `catalogService`, obwohl er von dort kommt: die
 * Seitentests bilden den Service mit `jest.mock` nach und listen darin nur
 * die Funktionen auf, die sie brauchen. Eine Konstante von dort waere in
 * jedem dieser Tests `undefined` -- und der Vergleich darauf stillschweigend
 * immer falsch. Dieses Modul wird nirgends nachgebildet.
 *
 * Derselbe Wert steht serverseitig in `data_catalog/views.py`
 * (`ARCHETYP_OHNE_ZUORDNUNG`) und wird von `filter_openehr_code` als Filter
 * akzeptiert -- Zaehlung, Beschriftung und Filter benutzen damit dieselbe
 * Zeichenkette.
 */
export const ARCHETYP_OHNE_ZUORDNUNG = 'keiner';

export const tagIconOptions = [
  { name: 'MonitorHeart', icon: MonitorHeartIcon, label: 'Vitalparameter' },
  { name: 'Science', icon: ScienceIcon, label: 'Labor' },
  { name: 'MedicalServices', icon: MedicalServicesIcon, label: 'Klinisch' },
  { name: 'Air', icon: AirIcon, label: 'Beatmung' },
  { name: 'Favorite', icon: FavoriteIcon, label: 'Kardiologie' },
  { name: 'Psychology', icon: PsychologyIcon, label: 'Neurologie' },
  { name: 'Radar', icon: RadarIcon, label: 'Monitoring' },
  { name: 'Vaccines', icon: VaccinesIcon, label: 'Medikation' },
  { name: 'Biotech', icon: BiotechIcon, label: 'Mikrobiologie' },
  { name: 'Bloodtype', icon: BloodtypeIcon, label: 'BGA' },
  { name: 'Timeline', icon: TimelineIcon, label: 'Zeitreihe' },
  { name: 'Assessment', icon: AssessmentIcon, label: 'Score' },
  { name: 'CameraAlt', icon: CameraAltIcon, label: 'Bildgebung' },
  { name: 'Coronavirus', icon: CoronavirusIcon, label: 'COVID-19' },
  { name: 'LocalHospital', icon: LocalHospitalIcon, label: 'Station' },
  { name: 'LocalOffer', icon: LocalOfferIcon, label: 'Allgemein' },
];

/**
 * Get icon component by name
 * @param {string} iconName - Name of the icon
 * @returns {React.Component|null} Icon component or null
 */
export const getIconComponent = (iconName) => {
  const iconOption = tagIconOptions.find(opt => opt.name === iconName);
  return iconOption ? iconOption.icon : null;
};

/**
 * Get OMOP status color for MUI Chip.
 *
 * Der Wertevorrat der Katalogkarte ist validated/suggested/not_mapped (#247,
 * #248) -- 'mapped'/'pending' gehoeren zum Workflow-Status des Mapping-Tools
 * (OmopMappingDetail.js) und sind hier absichtlich NICHT abgedeckt: wer sie
 * hier behandelte, faellt genau in die Falle, die #248 beheben sollte, sobald
 * dieser fremde Wert irgendwo durchsickert -- Unbekannt statt eines
 * plausiblen, aber falschen Zustands.
 * @param {string} status - OMOP mapping status ('validated', 'suggested', 'not_mapped')
 * @returns {string} MUI color name
 */
export const getOMOPStatusColor = (status) => {
  switch (status) {
    case 'validated':
      return 'success';
    default:
      return 'default';
  }
};

/**
 * Get OMOP status label in German
 * @param {string} status - OMOP mapping status
 * @returns {string} German label
 */
export const getOMOPStatusLabel = (status, t) => {
  const translate = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

  switch (status) {
    case 'validated':
      return translate('dataCatalog.omopStatus.validated', 'Validiert');
    case 'suggested':
      return translate('dataCatalog.omopStatus.suggested', 'Vorgeschlagen');
    case 'not_mapped':
      return translate('dataCatalog.omopStatus.notMapped', 'Nicht gemappt');
    default:
      return translate('dataCatalog.omopStatus.unknown', 'Unbekannt');
  }
};

/**
 * Get primary name based on view mode
 *
 * **Kein Rueckfall auf den lokalen Namen (#246).** Bis 2026-08-31 stand hier
 * `param.omopName || param.name`. Ein Parameter ohne Zuordnung zeigte damit in
 * der OMOP-Ansicht seinen Charite-Namen -- an der Stelle, an der ein
 * OMOP-Konzept steht, ohne jeden Hinweis darauf. Auf hospital1 traf das **alle
 * 3900 Karten** (`mappedCount: 0`): die Ansicht sah aus wie ein vollstaendig
 * zugeordneter Katalog.
 *
 * Dieselbe Fehlerklasse wie #217 (falsches ICD-Woerterbuch) und #226
 * (`units.sources`): plausibel und falsch, im Ergebnis nicht als Fehler
 * erkennbar. Ein leeres Feld haette jeder gesehen.
 *
 * @param {Object} param - Parameter object
 * @param {string} nameView - Ansicht: 'charite' | 'omop' | 'openehr' | 'alle'
 * @returns {string|null} Primary name, oder null in der OMOP-Ansicht ohne Zuordnung
 */
export const getPrimaryName = (param, nameView) => {
  // **In der openEHR-Ansicht bleibt der lokale Name der Hauptname** (#787).
  //
  // Bis hierher stand dort `openehrName` -- und das ist der **Archetypname**,
  // also die Struktur, in der der Wert steht: "Laboratory test analyte",
  // "Procedure", "Medication". Auf hospital1 hiessen damit 308 Parameter
  // "Procedure" und 581 "Problem diagnosis"; auswaehlen konnte man so keinen.
  //
  // Der Archetyp ist die Kategorie, nicht der Parameter. Was einen Parameter
  // in openEHR identifiziert, ist der **kodierte Wert am Konzeptpfad**
  // (LOINC, ICD) -- und wo es den nicht gibt, bleibt der lokale Name das
  // einzige, was ihn benennt. Er steht deshalb vorn; Archetyp und Code stehen
  // in der Zuordnungsspalte daneben, wo sie hingehoeren.
  //
  // Das ist **kein** Rueckfall im Sinne von #246: dort ging es darum, einen
  // fehlenden OMOP-Namen durch den lokalen zu ersetzen und so eine Zuordnung
  // vorzutaeuschen, die es nicht gibt. Hier wird nichts vorgetaeuscht -- die
  // openEHR-Zuordnung steht vollstaendig in ihrer eigenen Spalte.
  if (nameView === ANSICHT_OPENEHR) return param.name;
  if (nameView === 'omop') return param.omopName || null;
  return param.name;
};

/**
 * Get secondary name for dual view mode
 *
 * Gibt in der Ansicht `both` den OMOP-Namen zurueck -- `null`, wenn keiner da
 * ist. Der leere String, den die API heute liefert, ist hier **keine** Antwort:
 * er erzeugt eine leere Zeile, die aussieht wie ein Darstellungsfehler und
 * nicht wie eine fehlende Zuordnung (#246).
 *
 * @param {Object} param - Parameter object
 * @param {string} nameView - View mode
 * @returns {string|null} Secondary name or null
 */
export const getSecondaryName = (param, nameView) => {
  if (nameView === ANSICHT_ALLE) {
    return param.omopName || null;
  }
  return null;
};

/**
 * Get primary description based on view mode
 * @param {Object} param - Parameter object
 * @param {string} nameView - View mode
 * @returns {string} Primary description
 */
export const getPrimaryDescription = (param, nameView) => {
  if (nameView === 'omop') return param.omopDescription || param.description;
  if (nameView === ANSICHT_OPENEHR) return param.description;
  return param.description;
};

/**
 * Herkunft einer Zuordnung (#257). Der Backend-Wert `omopMappingSource`
 * traegt data_catalog/models.py's `source`-Enum ('import', 'ai', 'user',
 * 'manual', 'system'); `omopStatus === 'validated'` ist die eigene, spaeter
 * gesetzte Freigabe und geht ihr vor -- ein importierter oder KI-Vorschlag,
 * den ein Mensch geprueft hat, zeigt "validiert", nicht seine urspruengliche
 * Herkunft.
 */
export const OMOP_MAPPING_SOURCE_IMPORT = 'import';
export const OMOP_MAPPING_SOURCE_AI = 'ai';
export const OMOP_MAPPING_SOURCE_USER = 'user';
export const OMOP_MAPPING_SOURCE_MANUAL = 'manual';
export const OMOP_MAPPING_SOURCE_SYSTEM = 'system';
export const OMOP_MAPPING_PROVENANCE_VALIDATED = 'validated';

/**
 * Welche Herkunft-Kachel eine Karte zeigt -- oder `null`, wenn die API nichts
 * dazu sagt. **Kein Rueckfall auf einen erfundenen Wert (#247/#248, #246):**
 * fehlt sowohl der validierte Status als auch `omopMappingSource`, ist das
 * Ergebnis `null`, nicht "System".
 *
 * @param {Object} param - Parameter object (aus transformParameter)
 * @returns {string|null} 'validated' | 'import' | 'ai' | 'user' | 'manual' | 'system' | null
 */
export const getMappingProvenance = (param) => {
  if (!param) return null;
  if (param.omopStatus === OMOP_MAPPING_PROVENANCE_VALIDATED) {
    return OMOP_MAPPING_PROVENANCE_VALIDATED;
  }
  return param.omopMappingSource || null;
};

/**
 * Die Arbeitsliste aus AK4: von der KI vorgeschlagen, von niemandem geprueft.
 *
 * Ein abgelehnter KI-Vorschlag (`omopStatus === 'rejected'`) hat einen
 * Menschen bereits gesehen -- er gehoert nicht in "niemand hat das geprueft".
 * `getMappingProvenance` allein kennt "rejected" nicht (nur `validated` geht
 * der Herkunft vor); ohne diesen Ausschluss landete ein geprueft-und-verworfener
 * Vorschlag wieder in genau der Arbeitsliste, die ihn aussortieren soll.
 *
 * @param {Object} param - Parameter object
 * @returns {boolean}
 */
export const isUnvalidatedAiSuggestion = (param) =>
  getMappingProvenance(param) === OMOP_MAPPING_SOURCE_AI && param?.omopStatus !== 'rejected';

/**
 * Uebersetzte Kurzbezeichnung fuer die Herkunft-Chip in der Liste.
 *
 * @param {string|null} provenance - Rueckgabe von getMappingProvenance
 * @param {Function} t - i18next translate function
 * @returns {string|null} Label, oder null, wenn keine Herkunft bekannt ist
 */
export const getMappingProvenanceLabel = (provenance, t) => {
  const translate = (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

  switch (provenance) {
    case OMOP_MAPPING_PROVENANCE_VALIDATED:
      return translate('dataCatalog.mappingProvenance.validated', 'Vom Menschen validiert');
    case OMOP_MAPPING_SOURCE_IMPORT:
      return translate('dataCatalog.mappingProvenance.import', 'Importiert');
    case OMOP_MAPPING_SOURCE_AI:
      return translate('dataCatalog.mappingProvenance.ai', 'KI-Vorschlag');
    case OMOP_MAPPING_SOURCE_USER:
      return translate('dataCatalog.mappingProvenance.user', 'Manuell zugeordnet');
    case OMOP_MAPPING_SOURCE_MANUAL:
      return translate('dataCatalog.mappingProvenance.manual', 'Manuell zugeordnet');
    case OMOP_MAPPING_SOURCE_SYSTEM:
      return translate('dataCatalog.mappingProvenance.system', 'Systemzuordnung');
    default:
      return null;
  }
};

/**
 * MUI-Farbe je Herkunft. Der KI-Vorschlag bleibt bewusst 'warning' (#257
 * AK3): er darf nicht wie eine geprüfte Zuordnung aussehen.
 *
 * @param {string|null} provenance - Rueckgabe von getMappingProvenance
 * @returns {string} MUI Chip-Farbe
 */
export const getMappingProvenanceColor = (provenance) => {
  switch (provenance) {
    case OMOP_MAPPING_PROVENANCE_VALIDATED:
      return 'success';
    case OMOP_MAPPING_SOURCE_IMPORT:
      return 'info';
    case OMOP_MAPPING_SOURCE_AI:
      return 'warning';
    default:
      return 'default';
  }
};

/* ------------------------------------------------------------------ *
 * Die zweite Darstellung: openEHR (#772, Epic #769 / EHRCON26)
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Eine Ansicht, vier Stellungen (#782)
 * ------------------------------------------------------------------ */

/**
 * **Ein Umschalter, nicht zwei.** Bis #782 gab es `nameView`
 * (Lokal/OMOP/Beide) fuer den *Namen* und `darstellung` (OMOP/openEHR) fuer
 * die *Zuordnung* -- zwei Achsen fuer eine Frage, die der Nutzer als eine
 * stellt. Die Trennung stammt aus #772 und hatte dort ihren Grund: ein
 * gefalteter Schalter haette in Stellung "Lokal" die openEHR-Ansicht mit
 * ausgeschaltet. Mit vier Stellungen faellt dieser Einwand weg.
 *
 * Die Werte heissen weiter wie die alten, wo es sie schon gab (`charite`,
 * `omop`) -- ein reiner Umbenennungsdurchlauf durch acht Dateien haette
 * Aenderungsrisiko ohne Gegenwert.
 */
export const ANSICHT_LOKAL = 'charite';
export const ANSICHT_OMOP = 'omop';
export const ANSICHT_OPENEHR = 'openehr';
export const ANSICHT_ALLE = 'alle';

/** Zeigt diese Stellung die OMOP-Zuordnung? */
export const zeigtOmop = (ansicht) => ansicht === ANSICHT_OMOP || ansicht === ANSICHT_ALLE;

/** Zeigt diese Stellung die openEHR-Zuordnung? */
export const zeigtOpenEhr = (ansicht) => ansicht === ANSICHT_OPENEHR || ansicht === ANSICHT_ALLE;

/**
 * Die Statuswerte, die als "zugeordnet" gelten.
 *
 * **Nicht nur `validated`.** Auf hospital1 sind am 2026-09-18 alle 455
 * zugeordneten Parameter `suggested` und **null** `validated`; ein Filter auf
 * `validated` allein lieferte dort eine leere Seite. Der Katalogserver nimmt
 * mehrere Werte durch Komma getrennt entgegen (data_catalog/filters.py,
 * `filter_case_insensitive` -> `_split_values`), nachgeprueft an der laufenden
 * Instanz: `omop_status=validated,suggested` ergibt 455, ohne Filter 3.900.
 */
export const ZUGEORDNET = 'validated,suggested';

/**
 * Welcher Statusfilter gilt in dieser Stellung erzwungenermassen?
 *
 * In Stellung OMOP bzw. openEHR zeigt die Liste **nur** Parameter mit der
 * jeweiligen Zuordnung -- so entschieden am 2026-09-18. In den Stellungen
 * `lokal` und `alle` wird nicht eingeschraenkt; `alle` ist damit die einzige
 * Ansicht, in der ein Parameter mit openEHR- und ohne OMOP-Zuordnung sichtbar
 * bleibt. Faellt diese Ausnahme, faellt die Aussage des Vortrags mit ihr.
 */
export const erzwungenerStatusfilter = (ansicht) => {
  if (ansicht === ANSICHT_OMOP) return { omopStatus: ZUGEORDNET };
  if (ansicht === ANSICHT_OPENEHR) return { openehrStatus: ZUGEORDNET };
  return {};
};

/**
 * Laesst sich dieser Parameter von openEHR nach OMOP abbilden?
 *
 * **Drei Antworten, nicht zwei.** Das ist der ganze Punkt des Vortrags: ein
 * leeres Feld sagt "wir wissen es nicht", eine Kennzeichnung sagt "es geht
 * nicht". Wer beides zusammenwirft, blaeht genau die Zahl auf, mit der auf der
 * Buehne argumentiert wird.
 *
 * Entschieden wird ausschliesslich am gemessenen Kennzeichen aus der Pipeline
 * (`openehr_mapping.in_omop_abbildbar`, hier als `inOmopAbbildbar`). Aus der
 * Statuskombination `openehrStatus=validated & omopStatus=not_mapped` wird
 * **nicht** geschlossen: `not_mapped` heisst "niemand hat zugeordnet" und nicht
 * "es ist nicht zuordenbar" -- das eine ist ein offener Arbeitsschritt, das
 * andere ein Befund.
 *
 * @returns {'mappable'|'not_mappable'|'unassessed'}
 */
export const getOmopAbbildbarkeit = (param) => {
  if (param?.inOmopAbbildbar === true) {
    return 'mappable';
  }
  if (param?.inOmopAbbildbar === false) {
    return 'not_mappable';
  }
  return 'unassessed';
};

/**
 * Der Archetyp als Hauptbezeichner der openEHR-Darstellung.
 *
 * Wie `getPrimaryName` in der OMOP-Ansicht: **kein Rueckfall auf den lokalen
 * Namen** (#246). Fehlt der Archetyp, liefert diese Funktion null, und die
 * Oberflaeche zeigt eine Leerstelle -- nicht den Charite-Namen an der Stelle,
 * an der ein Archetypbezeichner steht.
 */
export const getOpenEhrPrimaryName = (param) => param?.openehrName || param?.openehrCode || null;

/** Die AQL-Pfade eines Parameters, leere Liste wenn keine bekannt sind. */
export const getAqlPfade = (param) => {
  const ausBlock = param?.openehrMapping?.aqlPfade;
  if (Array.isArray(ausBlock)) {
    return ausBlock.filter(Boolean);
  }
  return Array.isArray(param?.openehrAqlPfade) ? param.openehrAqlPfade.filter(Boolean) : [];
};

/**
 * Wie oft faellt ein openEHR-Konzept in OMOP auseinander?
 *
 * Das Abstract nennt Problem/Diagnosis mit ueber 20 verknuepften Datensaetzen.
 * Die Zahl wird nur gezeigt, wenn sie groesser als 1 ist: bei genau einer
 * Entsprechung gibt es nichts zu berichten, und eine "Fragmentierung: 1" laese
 * sich als Mangel missverstehen.
 */
export const getOmopFragmentierung = (param) => {
  const wert = param?.omopFragmentierung ?? param?.openehrMapping?.omopFragmentierung;
  return Number.isFinite(wert) && wert > 1 ? wert : null;
};
